import type { ReleaseRun, VersionInfo } from '@/api/releaseApproval';

/**
 * State of the test version and production as the two version cards show it
 * (#3481). A green card means nothing waits; yellow means something does:
 *
 *   same version in both          test synced     production current
 *   release to production running test releasing  production releasing
 *   test differs from production  test awaiting   production current
 *
 * Production's version can be unknown (its public endpoint unreachable); the
 * test version then still waits, but production gets no colour.
 */
export type TestVersionStatus = 'synced' | 'releasing' | 'awaiting';
export type ProductionVersionStatus = 'current' | 'releasing' | 'unknown';

export interface VersionStatuses {
  readonly test: TestVersionStatus;
  readonly production: ProductionVersionStatus;
}

export function versionStatuses(input: {
  readonly candidate: VersionInfo;
  readonly production: VersionInfo | null;
  readonly latestRun: ReleaseRun | null;
}): VersionStatuses {
  const running = input.latestRun?.status === 'queued' || input.latestRun?.status === 'in_progress';
  if (running) return { test: 'releasing', production: 'releasing' };
  if (!input.production?.sha) return { test: 'awaiting', production: 'unknown' };
  if (input.candidate.sha !== null && input.candidate.sha === input.production.sha) {
    return { test: 'synced', production: 'current' };
  }
  return { test: 'awaiting', production: 'current' };
}

/** Theme palette per status: never a hex value (portal lint gate). */
export const STATUS_PALETTE = {
  synced: 'success',
  current: 'success',
  releasing: 'warning',
  awaiting: 'warning',
  unknown: null,
} as const satisfies Record<TestVersionStatus | ProductionVersionStatus, 'success' | 'warning' | null>;
